const errorPayloadPromises = new WeakMap();

const parseErrorPayload = async (rawPayload) => {
  try {
    if (typeof rawPayload?.json === 'function') {
      return await rawPayload.json();
    }

    if (typeof rawPayload === 'string') {
      return JSON.parse(rawPayload);
    }

    return rawPayload ?? null;
  } catch (parseError) {
    return null;
  }
};

// WeakMap is used to cache error object parsing (objects are GC'd)
const getRestApiErrorPayload = (error) => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  if (!errorPayloadPromises.has(error)) {
    // The actual catch error object with stream object
    const rawPayload = error.response?.data ?? error.response?.body;
    errorPayloadPromises.set(error, parseErrorPayload(rawPayload));
  }

  return errorPayloadPromises.get(error);
};

/**
 * Formats an API or JavaScript error for display.
 *
 * Parsing an Amplify API error consumes its response body stream. The parsed payload is cached
 * for subsequent calls to this helper, but callers cannot read the original body stream afterward.
 */
const createErrorMessage = async (error, printStack = false) => {
  const payload = await getRestApiErrorPayload(error);
  const message = payload?.error?.message
    ?? payload?.message
    ?? error?.message // This may be a JavaScript exception message.
    ?? 'Unknown error';
  const code = payload?.error?.code ?? payload?.code;

  return `${message}${code ? ` (code ${code})` : ''}${printStack ? `\n\n${error?.stack}` : ''}`;
};

const mockApiError = (message, code = '') => ({
  message: 'Bad Request',
  response: {
    statusCode: 400,
    body: {
      json: async () => ({
        statusCode: 400,
        error: 'Bad Request',
        code,
        message,
      }),
    },
  },
});

const getDefaultFunctions = functions => Object.keys(functions)
  .reduce((result, key) => ({
    ...result,
    [key]: () => { },
  }), {});

export {
  createErrorMessage,
  getDefaultFunctions,
  mockApiError,
};
